/**
 * The roadmap: four columns derived from dates rather than stored on the record.
 *
 * A board is the right shape here and stays a board - the question it answers is
 * "what is running and what is next", which is a comparison between columns, not
 * a scan down one. What changed is the surfaces: the columns are the CRM's card
 * and the cards are its rows, instead of four pastel tints that meant nothing to
 * anyone who had not read the colour map.
 *
 * This file owns what a board contains and what a move writes back; the column
 * shell and the three card layouts live in `RoadmapCard.tsx`.
 *
 * Two things the board used to leave unsaid, and now says:
 *
 *  - a cancelled project has no column (`roadmapColumn` answers null), so
 *    switching from the list to the board quietly changed how many projects
 *    existed. The count of what the board is not showing goes underneath it.
 *  - a move is a WRITE, and one of the four columns writes more than the card
 *    shows: dropping on Completed makes the server stamp `completedAt`. So a
 *    move now leaves a receipt saying what was written, with the way back.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  getTasks, setTaskStatus, updateProject,
  type Milestone, type Project, type Task, type TaskStatus,
} from "../../../lib/projects";
import { Btn } from "../../ui";
import { ScrollFade } from "../_shared/ScrollFade";
import { Column, ColumnEmpty, MilestoneCard, ProjectCard, TaskCard } from "./RoadmapCard";
import { useMilestoneToggle } from "./useMilestoneToggle";
import {
  ROADMAP_ORDER, isOverdue, milestoneColumn, roadmapColumn, roadmapPatch, taskColumn, type RoadmapCol,
} from "./lib";

/**
 * What a move actually wrote, one sentence per column.
 *
 * A `Record` rather than a switch or a ternary chain: a fifth column added to
 * `RoadmapCol` fails the typecheck here instead of falling through to the last
 * branch and describing itself as somebody else.
 */
const MOVED_KEY: Record<RoadmapCol, string> = {
  completed: "projects.roadmap.moved.completed",
  in_progress: "projects.roadmap.moved.in_progress",
  planned: "projects.roadmap.moved.planned",
  future: "projects.roadmap.moved.future",
};

/**
 * A milestone nobody finished and whose date has gone by.
 *
 * The same one-line rule is read in three places (the list row, the board card,
 * the column counter below). Its home is `projects/lib.ts` next to `isOverdue`,
 * which is a shared file this ring is being edited by somebody else - see the
 * report.
 */
function milestoneLate(m: Milestone): boolean {
  return !m.done && !!m.dueDate && new Date(m.dueDate) < new Date();
}

/**
 * A project sitting in the Planned column whose deadline has already passed.
 *
 * Only the deadline is tested, not the status: every card in that column is
 * `not_started` by construction (`roadmapColumn`), so the "still something to
 * deliver" test the register's rows have to make is already made here.
 */
function plannedLate(p: Project): boolean {
  return !!p.deadline && new Date(p.deadline) < new Date();
}

/** The last move, kept so the board can say what it wrote and put it back. */
interface MoveReceipt {
  text: string;
  /** The record as it was BEFORE the move - the only copy of the fields the
   *  write replaced. */
  before: Project;
}

// ── The portfolio board ─────────────────────────────────────────────────────

/**
 * Every project, grouped by when it happens. Moving a card writes the status
 * (and, for the two not_started columns, the start date) straight back - see
 * `roadmapPatch`.
 */
export function RoadmapBoard({ rows, onChanged, onReload }: {
  rows: Project[];
  onChanged: (next: Project) => void;
  onReload: () => void;
}) {
  const t = useT();
  const [receipt, setReceipt] = useState<MoveReceipt | null>(null);

  function move(p: Project, col: RoadmapCol) {
    const patch = roadmapPatch(col);
    // Optimistic: the card has to land in the new column under the cursor, not
    // after a round trip. A failed write reloads rather than guessing.
    onChanged({ ...p, ...patch } as Project);
    updateProject(p.id, patch)
      .then((saved) => {
        // The server's copy, not the optimistic guess. Moving to Completed makes
        // the server fill `completedAt`, and the card would otherwise carry a
        // completion date of null until something else reloaded it.
        onChanged(saved);
        setReceipt({ text: t(MOVED_KEY[col]), before: p });
      })
      .catch(onReload);
  }

  function undo(before: Project) {
    setReceipt(null);
    onChanged(before);
    // Every field the move could have touched, named out loud. `completedAt` in
    // particular: the server sets it on its own when a project becomes
    // completed, so an undo that only put the status back would leave the
    // project finished on one column and dated on another.
    updateProject(before.id, {
      status: before.status,
      startDate: before.startDate,
      completedAt: before.completedAt,
    })
      .then(onChanged)
      .catch(onReload);
  }

  // Cancelled projects are in `rows` and belong to no column, so the board shows
  // fewer records than the list did with the same query.
  const hidden = rows.filter((p) => roadmapColumn(p) === null).length;

  return (
    <>
      <ScrollFade className="pb-4" deps={[rows]}>
        <div className="flex min-w-max gap-3.5">
          {ROADMAP_ORDER.map((key) => {
            const items = rows.filter((p) => roadmapColumn(p) === key);
            const late = key === "planned" ? items.filter(plannedLate).length : 0;
            return (
              <Column
                key={key}
                tone={key}
                label={t(`projects.roadmap.${key}`)}
                count={items.length}
                overdue={late > 0 ? t("projects.roadmap.overdue", { n: late }) : null}
              >
                {items.map((p) => (
                  <ProjectCard key={p.id} t={t} p={p} onMove={move} />
                ))}
                {items.length === 0 ? <ColumnEmpty label={t("projects.roadmap.empty")} /> : null}
              </Column>
            );
          })}
        </div>
      </ScrollFade>

      {/* The two things the board owes the operator once the cards are drawn:
          what it is not showing, and what the last click wrote.

          The receipt is a line under the board rather than a toast because the
          toast is something you read - `.crm-toast` is `pointer-events: none`
          and clears itself after 2.8 seconds, which is not long enough to find
          an Undo, let alone decide to use one. It stays until it is used or
          until the next move replaces it. */}
      {receipt || hidden > 0 ? (
        // Two statements of different kinds, so two lines: one is a receipt for
        // something the operator just did, the other is a standing fact about
        // the board. Side by side they would read as one sentence.
        <div className="flex flex-col items-start gap-2 px-1.5 pb-1">
          {receipt ? (
            <span className="flex flex-wrap items-center gap-2.5">
              <span className="crm-mut text-[12.25px]">{receipt.text}</span>
              <Btn small onClick={() => undo(receipt.before)}>
                {t("projects.roadmap.undo")}
              </Btn>
            </span>
          ) : null}
          {hidden > 0 ? (
            <span className="crm-mut text-[12.25px]">{t("projects.roadmap.cancelledHidden", { n: hidden })}</span>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

// ── The in-project board ────────────────────────────────────────────────────

/**
 * One project's tasks and milestones in the same four columns.
 *
 * A milestone has no "in progress" state, so an unfinished one is placed by its
 * due date and carries a milestone chip - otherwise the two kinds of card are
 * indistinguishable and the board looks like it has twice as many tasks as it
 * has.
 */
export function ProjectRoadmap({ project, onChange }: {
  project: Project & { milestones: Milestone[] };
  onChange: () => void;
}) {
  const t = useT();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>(project.milestones);
  const [loading, setLoading] = useState(true);
  const toggleMilestone = useMilestoneToggle(setMilestones, onChange);

  useEffect(() => {
    setMilestones(project.milestones);
  }, [project.milestones]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getTasks({ projectId: project.id, pageSize: 100 });
      setTasks(r.rows);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function moveTask(id: string, s: TaskStatus) {
    setTasks((prev) => prev.map((x) => (x.id === id ? { ...x, status: s } : x)));
    try {
      await setTaskStatus(id, s);
      onChange();
    } catch {
      void load();
    }
  }

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    <ScrollFade className="pb-2" deps={[tasks, milestones]}>
      <div className="flex min-w-max gap-3.5">
        {ROADMAP_ORDER.map((key) => {
          const ms = milestones.filter((m) => milestoneColumn(m) === key);
          const ts = tasks.filter((tk) => taskColumn(tk) === key);
          const n = ms.length + ts.length;
          // Planned is where lateness hides on this board: an overdue milestone
          // stays in Planned on purpose (it is late, not further away), and a
          // task keeps its column until somebody moves it.
          const late = key === "planned" ? ms.filter(milestoneLate).length + ts.filter(isOverdue).length : 0;
          return (
            <Column
              key={key}
              tone={key}
              label={t(`projects.roadmap.${key}`)}
              count={n}
              overdue={late > 0 ? t("projects.roadmap.overdue", { n: late }) : null}
            >
              {ms.map((m) => (
                <MilestoneCard key={`m-${m.id}`} t={t} m={m} onToggle={toggleMilestone} />
              ))}

              {ts.map((tk) => (
                <TaskCard key={`t-${tk.id}`} t={t} tk={tk} onMove={(id, s) => void moveTask(id, s)} />
              ))}

              {n === 0 ? <ColumnEmpty label={t("projects.roadmap.emptyItems")} /> : null}
            </Column>
          );
        })}
      </div>
    </ScrollFade>
  );
}
